import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useDataProvider } from 'ra-core';
import { useNotify } from 'react-admin';
import FileSaver from 'file-saver';
import Loader from '../common/loader';
import { ReportItem } from '../../types/report';
import './style.css';
import PaginationTable from '../common/paginationTable';
import { getDate } from '../../helpers/dateHelper';
import HeadTable from '../common/sortTable/sortTable';
import { getFieldByTitle } from '../../helpers/sortByTitle';

const TEN_SECONDS_MS = 10000;
const STATUSES_WITH_PROGRESS = new Set([
  'IN_PROGRESS',
  'PENDING',
  'FAILED',
  'ERROR',
]);

const normalizeType = (type: string) => type.split(';')[0];
const getExtensionFromType = (type: string) => {
  const map: Record<string, string> = {
    'application/zip': 'zip',
    'text/csv': 'csv',
    'application/pdf': 'pdf',
    'application/json': 'json',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'text/plain': 'txt',
    'image/png': 'png',
    'image/jpeg': 'jpg',
  };

  return map[type] || 'csv'; // fallback
};
const convertToBlob = (data, type, fileName) => {
  // let blob = new Blob([data], { type });
  // fileName =
  //   blob.type == 'application/zip' ? `${fileName}.zip` : `${fileName}.csv`;
  // FileSaver.saveAs(blob, fileName);
  const blob = new Blob([data], { type });

  const ext = getExtensionFromType(normalizeType(type));
  const finalName = `${fileName}.${ext}`;

  FileSaver.saveAs(blob, finalName);
};

interface State {
  total: number;
  page: number;
  perPage: number;
  field: string;
  order: string;
}

const ReportsList: FC = () => {
  const notify = useNotify();

  const dataProvider = useDataProvider();

  const [reports, setReports] = useState<Array<ReportItem>>([]);
  const [state, setState] = useState<State>({
    total: 0,
    page: 1,
    perPage: 10,
    field: 'createdAt',
    order: 'desc',
  });
  const paramsRef = useRef(state);
  const [isDownloading, setIsDownloading] = useState(false);

  const getReportList = async (
    page: number,
    perPage: number,
    field: string,
    order: string,
  ) => {
    const result: any = await dataProvider.getReports({
      filter: {},
      pagination: { page, perPage },
      sort: { field: field, order: order },
    });
    setReports(result.data.reports);
    setState({
      page,
      perPage,
      field,
      order,
      total: result.data.allCount,
    });
  };

  const download = async (id: string, name: string) => {
    if (!isDownloading) {
      setIsDownloading(true);
      const result: any = await dataProvider.downloadReport(id);
      console.log('result: ', result.data);
      convertToBlob(
        result.data.data,
        result.data.headers['content-type'],
        name,
      );
      const timeout = setTimeout(() => {
        setIsDownloading(false);
      }, 3000);
    }
  };

  useEffect(() => {
    paramsRef.current = state;
  }, [state]);

  useEffect(() => {
    getReportList(state.page, state.perPage, state.field, state.order);
  }, []);

  const hasInProgress = reports.some(
    (r) => r.status === 'IN_PROGRESS' || r.status === 'PENDING',
  );
  useEffect(() => {
    if (!hasInProgress) return;

    const interval = setInterval(() => {
      const { page, perPage, field, order } = paramsRef.current;

      getReportList(page, perPage, field, order);
    }, TEN_SECONDS_MS);

    return () => clearInterval(interval);
  }, [hasInProgress]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ): void => {
    getReportList(newPage + 1, state.perPage, state.field, state.order);
  };

  const handleLastPage = () => {
    const page = Math.ceil(state.total / state.perPage);
    getReportList(page, state.perPage, state.field, state.order);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const perPage = parseInt(event.target.value, 10);
    getReportList(state.page, perPage, state.field, state.order);
  };

  const handleFirstPage = () => {
    getReportList(1, state.perPage, state.field, state.order);
  };

  const handleSort = (direction: string, field: string) => {
    field = getFieldByTitle(field);
    getReportList(state.page, state.perPage, field, direction);
  };

  const formatStatus = (item: ReportItem): string => {
    if (!item.status) return '-';

    const shouldShowProgress =
      item.progress !== undefined && STATUSES_WITH_PROGRESS.has(item.status);

    return shouldShowProgress
      ? `${item.status} (${item.progress}%)`
      : item.status;
  };

  return (
    <Box>
      <Card>
        <Box padding='2em'>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell id='active-table-head'>
                    <HeadTable title='Name' sort={handleSort} />
                  </TableCell>
                  <TableCell id='active-table-head'>
                    <HeadTable title='Status' />
                  </TableCell>
                  <TableCell id='active-table-head'>
                    <HeadTable title='Created Date' sort={handleSort} />
                  </TableCell>
                  <TableCell id='active-table-head'>Finished Date</TableCell>
                  <TableCell id='active-table-head-actions'>Error</TableCell>
                  <TableCell id='active-table-head-actions'>Actions</TableCell>
                </TableRow>
              </TableHead>
              {reports.map((item, i) => {
                return (
                  <TableBody key={i}>
                    <TableRow className='test'>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{formatStatus(item)}</TableCell>
                      <TableCell>{getDate(new Date(item.createdAt))}</TableCell>
                      <TableCell>
                        {item.finishedAt
                          ? getDate(new Date(item.finishedAt))
                          : '-'}
                      </TableCell>
                      <TableCell>{item.error ? item.error : '-'}</TableCell>
                      <TableCell>
                        {(item.status === 'IN_PROGRESS' ||
                          item.status === 'PENDING') && (
                          <Box
                            display='flex'
                            justifyContent='center'
                            alignItems='center'
                          >
                            <Loader small />
                          </Box>
                        )}
                        {(item.status === 'ERROR' ||
                          item.status === 'FAILED') && (
                          <Box
                            display='flex'
                            justifyContent='center'
                            alignItems='center'
                          >
                            <span style={{ color: 'red' }}>Error</span>
                          </Box>
                        )}
                        {item.storeKey && (
                          <Box
                            display='flex'
                            justifyContent='center'
                            alignItems='center'
                            className={`${isDownloading ? 'disable-download' : 'download-icon'}`}
                            onClick={() => download(item._id, item.name)}
                          >
                            <GetAppIcon />
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                );
              })}
            </Table>
          </TableContainer>
          <PaginationTable
            page={state.page}
            perPage={state.perPage}
            collectionLength={reports.length}
            total={state.total}
            handleChangePage={handleChangePage}
            handleLastPage={handleLastPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleFirstPage={handleFirstPage}
            title='Reports'
          />
        </Box>
      </Card>
    </Box>
  );
};

export default ReportsList;
